import { useDetachCourseLessonFromStudent } from "hooks/api/courses/useDetachCourseLessonFromStudent";
import { useRole } from "hooks/common";
import { useActiveUser } from "hooks/redux/useActiveUser";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  StudentClassroomContent as Content,
  CourseDetailsContent,
} from "content";
import { useBreadcrumbs } from "hooks/redux";
import { useStudentById } from "hooks/api/students";
import {
  useAddCourseLessonToStudent,
  useCourseById,
  useCourseLessons,
} from "hooks/api/courses";
import { useAddLessonToStudentColumns } from "hooks/table/useAddLessonToStudentColumns";
import { Table } from "components/Table";
import { Empty } from "components/Common/Empty";
import { CourseHero } from "./components/CourseHero";
import { Route } from "constants/routes";
import { TStudentCourseLesson } from "types/api/course";
import "./StudentClassroomCourse.styles.scss";

export const StudentClassroomCourse: React.FC = () => {
  const { studentId, courseId } = useParams();
  const navigate = useNavigate();

  const [pageNumber, setPageNumber] = useState<number>(0);

  const { activeUser } = useActiveUser();
  const { isUserStudent } = useRole();

  const { setPageName, resetPageName } = useBreadcrumbs();
  const {
    data: course,
    isLoading: isCourseLoading,
    error,
  } = useCourseById(courseId);
  const { data: student } = useStudentById(studentId);

  // First request: Get student-specific lessons (using URL studentId)
  const {
    data: studentCourseLessonsData,
    isLoading: isStudentLessonsLoading,
    isSuccess: isStudentLessonsSuccess,
    error: studentLessonsError,
  } = useCourseLessons(courseId, {
    pageNumber,
    studentId,
  });

  // Second request: Get all lessons (conditional on first request success)
  const { data: courseLessonsData, isLoading: isLessonsLoading } =
    useCourseLessons(
      courseId,
      {
        pageNumber,
        pageSize: 99999,
        studentId: isUserStudent ? activeUser?.studentId : null,
      },
      {
        enabled:
          courseId !== undefined &&
          isStudentLessonsSuccess &&
          !studentLessonsError,
      }
    );
  const { mutate: addLessonToStudent } = useAddCourseLessonToStudent();
  const { mutate: detachCourseLessonFromStudent } =
    useDetachCourseLessonFromStudent();

  const courseLessons: TStudentCourseLesson[] = useMemo(
    () =>
      (courseLessonsData?.content || []).map((lesson) => ({
        ...lesson,
        isPinned:
          studentCourseLessonsData?.content?.some(
            (studentLesson) => studentLesson.id === lesson.id
          ) || false,
      })),
    [courseLessonsData?.content, studentCourseLessonsData?.content]
  );
  const isEmpty: boolean = useMemo(
    () => courseLessons.length === 0,
    [courseLessons]
  );
  const isLoading: boolean = useMemo(
    () => isLessonsLoading || isStudentLessonsLoading,
    [isLessonsLoading, isStudentLessonsLoading]
  );

  // Handle student lessons error - if first request fails, show error state
  const hasStudentLessonsError: boolean = useMemo(
    () => !!studentLessonsError,
    [studentLessonsError]
  );

  const pinLessonClickHandler = (courseLessonId: number) => {
    if (!studentId) return;

    addLessonToStudent({
      studentId: Number(studentId),
      courseId: courseId,
      lessonId: courseLessonId,
    });
  };

  const unpinLessonClickHandler = (courseLessonId: number) => {
    if (!studentId) return;

    detachCourseLessonFromStudent({
      studentId: Number(studentId),
      courseId: courseId,
      lessonId: courseLessonId,
    });
  };

  const viewLessonClickHandler = (courseLessonId: number) => {
    if (!studentId || !courseId) return;
    navigate(
      Route.App.Study.STUDENT_CLASSROOM_COURSE_LESSON.construct(
        studentId,
        courseId,
        courseLessonId.toString()
      )
    );
  };

  const columns = useAddLessonToStudentColumns(courseId, {
    onPinClick: pinLessonClickHandler,
    onUnPinClick: unpinLessonClickHandler,
    onViewClick: viewLessonClickHandler,
  });

  useEffect(() => {
    if (student?.person) {
      const fullName = `${Content.Name} ${student.person.firstName} ${student.person.lastName}`;
      setPageName(fullName);
    }
    return () => resetPageName();
  }, [student?.person, setPageName, resetPageName]);

  useEffect(() => {
    // TODO: handler properly not found resource error
    if (error) {
      navigate(Route.App.Materials.ROOT.path);
    }
  }, [error, navigate]);

  return (
    <>
      <CourseHero course={course} loading={isCourseLoading} />
      {hasStudentLessonsError ? (
        <Empty text="Failed to load student lessons. Please try again later." />
      ) : isEmpty && !isLoading ? (
        <Empty text={CourseDetailsContent.EmptyForStudent} />
      ) : (
        <Table<TStudentCourseLesson>
          data={courseLessons}
          columns={columns}
          loading={isLoading}
          totalItems={courseLessonsData?.totalElements}
          onPageChange={setPageNumber}
          hideHeader
          hideFooter
        />
      )}
    </>
  );
};
